<template>
  <section 
    id="contacto" 
    class="py-20 px-4 sm:px-6 lg:px-8" 
    style="background-color: hsl(var(--background)); position: relative; z-index: 10; pointer-events: auto;"
  >
    <div class="max-w-7xl mx-auto">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold mb-4" style="color: hsl(var(--foreground))">
          Contacto
        </h2>
        <p class="text-xl max-w-3xl mx-auto" style="color: hsl(var(--muted-foreground))">
          ¿Listo para transformar tu negocio? Contáctanos y descubre cómo podemos ayudarte
        </p>
      </div>

      <div class="grid lg:grid-cols-2 gap-12">
        <!-- Contact Form -->
        <div 
          class="p-8 rounded-xl shadow-lg border"
          style="background-color: hsl(var(--card)); border-color: hsl(var(--border))"
        >
          <h3 class="text-2xl font-bold mb-6" style="color: hsl(var(--foreground))">
            Envíanos un mensaje
          </h3>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div class="grid sm:grid-cols-2 gap-4">
              <div>
                <label class="text-sm font-medium mb-2 block" style="color: hsl(var(--foreground))">
                  Nombre *
                </label>
                <input
                  v-model="formData.name"
                  type="text"
                  placeholder="Tu nombre"
                  :class="inputStyle"
                  required
                />
              </div>
              <div>
                <label class="text-sm font-medium mb-2 block" style="color: hsl(var(--foreground))">
                  Empresa
                </label>
                <input
                  v-model="formData.company"
                  type="text"
                  placeholder="Nombre de la empresa"
                  :class="inputStyle"
                />
              </div>
            </div>
            
            <div>
              <label class="text-sm font-medium mb-2 block" style="color: hsl(var(--foreground))">
                Email *
              </label>
              <input
                v-model="formData.email"
                type="email"
                placeholder="tu@email.com"
                :class="inputStyle"
                required
              />
            </div>
            
            <div>
              <label class="text-sm font-medium mb-2 block" style="color: hsl(var(--foreground))">
                Teléfono
              </label>
              <input
                v-model="formData.phone"
                type="tel"
                placeholder="+52 55 1234 5678"
                :class="inputStyle"
              />
            </div>
            
            <div>
              <label class="text-sm font-medium mb-2 block" style="color: hsl(var(--foreground))">
                Mensaje *
              </label>
              <textarea
                v-model="formData.message"
                placeholder="Cuéntanos sobre tu proyecto..."
                rows="4"
                :class="`${inputStyle} resize-none`"
                required
              />
            </div>
            
            <button
              type="submit"
              :class="`w-full ${buttonStyle} flex items-center justify-center gap-2`"
            >
              <Send class="w-4 h-4" />
              Enviar mensaje
            </button>
          </form>
        </div>

        <!-- Contact Information -->
        <div class="space-y-8">
          <div>
            <h3 class="text-2xl font-bold mb-6" style="color: hsl(var(--foreground))">
              Información de contacto
            </h3>
            <div class="space-y-6">
              <div class="flex items-start space-x-4">
                <div class="p-3 rounded-lg" style="background-color: hsl(var(--primary) / 0.1)">
                  <Mail class="h-6 w-6" style="color: hsl(var(--primary))" />
                </div>
                <div>
                  <h4 class="font-semibold" style="color: hsl(var(--foreground))">Email</h4>
                  <a 
                    href="mailto:contacto@forzati.mx"
                    :class="linkStyle"
                  >
                    contacto@forzati.mx
                  </a>
                </div>
              </div>
              
              <div class="flex items-start space-x-4">
                <div class="p-3 rounded-lg" style="background-color: hsl(var(--primary) / 0.1)">
                  <Phone class="h-6 w-6" style="color: hsl(var(--primary))" />
                </div>
                <div>
                  <h4 class="font-semibold" style="color: hsl(var(--foreground))">Teléfono</h4>
                  <a 
                    href="tel:+525512345678"
                    :class="linkStyle"
                  >
                    +52 55 1234 5678
                  </a>
                </div>
              </div>
              
              <div class="flex items-start space-x-4">
                <div class="p-3 rounded-lg" style="background-color: hsl(var(--primary) / 0.1)">
                  <MapPin class="h-6 w-6" style="color: hsl(var(--primary))" />
                </div>
                <div>
                  <h4 class="font-semibold" style="color: hsl(var(--foreground))">Ubicación</h4>
                  <p style="color: hsl(var(--muted-foreground))">
                    Ciudad de México, México
                  </p>
                </div>
              </div>
              
              <div class="flex items-start space-x-4">
                <div class="p-3 rounded-lg" style="background-color: hsl(var(--primary) / 0.1)">
                  <Clock class="h-6 w-6" style="color: hsl(var(--primary))" />
                </div>
                <div>
                  <h4 class="font-semibold" style="color: hsl(var(--foreground))">Horario</h4>
                  <p style="color: hsl(var(--muted-foreground))">
                    Lunes a Viernes: 9:00 AM - 6:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- CTA Card -->
          <div 
            class="rounded-xl p-6 border"
            style="background-color: hsl(var(--primary) / 0.05); border-color: hsl(var(--primary) / 0.2)"
          >
            <h4 class="text-xl font-bold mb-3" style="color: hsl(var(--foreground))">
              ¿Necesitas una respuesta rápida?
            </h4>
            <p class="mb-4" style="color: hsl(var(--muted-foreground))">
              Agenda una llamada directamente con nuestro equipo
            </p>
            <button
              @click="handleCalendarClick"
              class="w-full bg-transparent border-2 text-primary hover:bg-primary hover:text-white transition-colors duration-300 font-medium px-6 py-3 rounded-lg"
              style="border-color: hsl(var(--primary)); color: hsl(var(--primary))"
            >
              Agendar llamada
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Send, Mail, Phone, MapPin, Clock } from 'lucide-vue-next'

const formData = ref({
  name: "",
  company: "",
  email: "",
  phone: "",
  message: ""
})

const inputStyle = "w-full p-3 rounded-lg border-2 border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white text-gray-900"
const buttonStyle = "bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer border-none"
const linkStyle = "text-orange-500 hover:text-orange-600 transition-colors cursor-pointer"

const handleSubmit = () => {
  if (!formData.value.name || !formData.value.email || !formData.value.message) {
    alert("Por favor completa todos los campos requeridos")
    return
  }

  alert("¡Mensaje enviado correctamente! Te contactaremos pronto.")
  console.log("Form data:", formData.value)
  
  // Reset form
  formData.value = {
    name: "",
    company: "",
    email: "",
    phone: "",
    message: ""
  }
}

const handleCalendarClick = () => {
  console.log("Opening calendar...")
  window.open('https://calendar.app.google/sm14g81D1o1j4peJ6', '_blank')
}
</script>